import type { Difficulty } from "@scb/ai";
import {
  ClientMessage,
  MATCH_FORMATS,
  ServerMessage,
  isMatchFormat,
  slotLabel,
  SHOT_LIMITS,
  type AimBroadcast,
  type ChatBroadcast,
  type FinalSequenceOfferPayload,
  type LaunchSide,
  type MatchEndedPayload,
  type RejectedPayload,
  type RoundEndReason,
  type RoundEndedPayload,
  type ShotInput,
  type ShotResultPayload,
  type TurnChangedPayload,
} from "@scb/protocol";
import { launchSideFor } from "@scb/rules";
import { DEFAULT_TABLE, launchPointFor, slotCounts } from "@scb/sim";
import {
  LOCAL_PRACTICE_SUMMARY,
  LocalSession,
  PRACTICE_BALLS,
  playersOf,
  type BattleView,
  type GameSession,
  type PracticeSummary,
  type SessionMode,
} from "@scb/client-core";
import { createRemoteSession } from "./net/room";
import { TableRenderer, type AimView } from "./render/renderer";

const $ = <T extends HTMLElement>(id: string): T => {
  const element = document.getElementById(id);
  if (!element) throw new Error(`#${id} 가 없습니다`);
  return element as T;
};

const el = {
  stage: $<HTMLDivElement>("stage"),
  hud: $<HTMLDivElement>("hud"),
  controls: $<HTMLDivElement>("controls"),
  lobby: $<HTMLDivElement>("lobby"),
  finalSequence: $<HTMLDivElement>("finalSequence"),
  practice: $<HTMLDivElement>("practice"),
  rules: $<HTMLDivElement>("rules"),
  rulesBody: $<HTMLDetailsElement>("rulesBody"),
  practiceStat: $<HTMLSpanElement>("practiceStat"),
  log: $<HTMLDivElement>("log"),

  score0: $<HTMLSpanElement>("score0"),
  score1: $<HTMLSpanElement>("score1"),
  balls0: $<HTMLSpanElement>("balls0"),
  balls1: $<HTMLSpanElement>("balls1"),
  phase: $<HTMLDivElement>("phase"),
  seat: $<HTMLDivElement>("seat"),
  turn: $<HTMLDivElement>("turn"),

  name: $<HTMLInputElement>("name"),
  mode: $<HTMLSelectElement>("mode"),
  format: $<HTMLSelectElement>("format"),
  difficulty: $<HTMLSelectElement>("difficulty"),
  knockout: $<HTMLInputElement>("knockout"),
  knockoutOpt: $<HTMLLabelElement>("knockoutOpt"),
  join: $<HTMLButtonElement>("join"),
  ready: $<HTMLButtonElement>("ready"),
  switchTeam: $<HTMLButtonElement>("switchTeam"),
  roster: $<HTMLDivElement>("roster"),
  status: $<HTMLDivElement>("status"),

  angle: $<HTMLInputElement>("angle"),
  power: $<HTMLInputElement>("power"),
  spin: $<HTMLInputElement>("spin"),
  pos: $<HTMLInputElement>("pos"),
  angleOut: $<HTMLOutputElement>("angleOut"),
  powerOut: $<HTMLOutputElement>("powerOut"),
  spinOut: $<HTMLOutputElement>("spinOut"),
  posOut: $<HTMLOutputElement>("posOut"),
  posLabel: $<HTMLSpanElement>("posLabel"),
  posCtl: $<HTMLLabelElement>("posCtl"),
  shoot: $<HTMLButtonElement>("shoot"),

  keyHint: $<HTMLDivElement>("keyHint"),
  jogTrack: $<HTMLDivElement>("jogTrack"),
  jogTicks: $<HTMLDivElement>("jogTicks"),
  jogDec: $<HTMLButtonElement>("jogDec"),
  jogInc: $<HTMLButtonElement>("jogInc"),
  rulesBtn: $<HTMLButtonElement>("rulesBtn"),
  rulesClose: $<HTMLButtonElement>("rulesClose"),

  turnClock: $<HTMLSpanElement>("turnClock"),
  oppAim: $<HTMLDivElement>("oppAim"),
  oppAimLabel: $<HTMLSpanElement>("oppAimLabel"),
  oppAngle: $<HTMLElement>("oppAngle"),
  oppPower: $<HTMLElement>("oppPower"),

  bank: $<HTMLButtonElement>("bank"),
  continue: $<HTMLButtonElement>("continue"),
  fsText: $<HTMLSpanElement>("fsText"),
  reset: $<HTMLButtonElement>("reset"),
  swapSeat: $<HTMLButtonElement>("swapSeat"),
  quit: $<HTMLButtonElement>("quit"),
  hubLink: $<HTMLAnchorElement>("hubLink"),

  invite: $<HTMLButtonElement>("invite"),
  roomCode: $<HTMLElement>("roomCode"),
  inviteHint: $<HTMLSpanElement>("inviteHint"),

  chat: $<HTMLDivElement>("chat"),
  chatToggle: $<HTMLButtonElement>("chatToggle"),
  chatBadge: $<HTMLElement>("chatBadge"),
  chatPop: $<HTMLDivElement>("chatPop"),
  chatPeek: $<HTMLDivElement>("chatPeek"),
  chatMountLobby: $<HTMLDivElement>("chatMountLobby"),
  chatBox: $<HTMLDivElement>("chatBox"),
  chatLog: $<HTMLDivElement>("chatLog"),
  chatForm: $<HTMLFormElement>("chatForm"),
  chatText: $<HTMLInputElement>("chatText"),
  chatTeam: $<HTMLButtonElement>("chatTeam"),
};

const SLOT_COUNTS = slotCounts(DEFAULT_TABLE);

// 조작계는 어디서나 **하단 독** 하나다 (구 "컴팩트 모드").
//
// 원래는 데스크톱(오른쪽 세로 패널 + 슬라이더)과 컴팩트(하단 독 + 조그)로
// 갈라져 있었는데, 같은 게임의 조작감이 기기마다 다른 게 싫어서 하단 독으로
// 통일했다. 조그·홀드 발사는 포인터 이벤트라 마우스로도 그대로 굴러가고,
// 키보드(← → · Space)도 여전히 듣는다. CSS 쪽 스위치였던 `<html data-compact>`는
// index.html에 박아 두었다.

/**
 * 연습 모드에서만 쓰는 위치 슬라이더 범위 (자리 고정이 없다).
 * 벽에 딱 붙으면 발사 즉시 벽에 걸리므로 공 반경보다 조금 더 띄운다.
 *
 * ⚠️ 아래 최상위 `syncSeat(null)` 호출보다 **먼저** 선언돼야 한다.
 * 함수 선언과 달리 const는 호이스팅되지 않아서, 뒤에 두면 TDZ로 모듈이 통째로 죽는다.
 */
const EDGE_MARGIN = DEFAULT_TABLE.ballRadius + 0.03;
const FREE_MIN_X = EDGE_MARGIN;
const FREE_MAX_X = DEFAULT_TABLE.width - EDGE_MARGIN;

const renderer = new TableRenderer(DEFAULT_TABLE);
let session: GameSession | null = null;
/**
 * 세션 세대 번호. start()/quit()는 await를 끼고 있어서, 기다리는 동안 사용자가
 * 다른 모드를 시작할 수 있다. 자기 세대가 아니면 공용 상태를 건드리지 않는다.
 */
let generation = 0;

/** 지금 내가 쏠 수 있는 페이즈인가 (턴 + 페이즈 조건). */
let myTurn = false;
/**
 * 공이 굴러가는 중 — 발사(또는 상대 샷 도착)부터 리플레이 재생이 끝날 때까지 true.
 *
 * 이 동안에는 **결과를 감춘다**: 점수·연습 통계를 올리지 않고 로그도 모아뒀다가
 * 재생이 끝나고 한꺼번에 내보낸다. 안 그러면 공이 아직 굴러가는데 몇 점인지
 * 먼저 알게 된다.
 *
 * `renderer.isReplaying`으로는 부족하다 — 세션은 `ShotResult`를 보내기 **전에**
 * 상태를 먼저 동기화하므로, 그 시점엔 아직 재생이 시작되지 않아 점수가 새어 나간다.
 *
 * 발사 버튼 잠금도 이 플래그가 겸한다. 연습 모드는 턴이 넘어가지 않아
 * `myTurn`만으로는 연타를 막을 수 없다.
 */
let shotInFlight = false;
/**
 * `shotInFlight`는 화면의 결과 은폐와 항상 같이 움직여야 한다.
 * 한쪽만 바꾸면 점수는 감췄는데 공은 실패로 보이는(또는 그 반대) 상태가 생긴다.
 */
function setShotInFlight(value: boolean): void {
  shotInFlight = value;
  renderer.setHideOutcome(value);
}

/** 이번 라운드에 내가 쓰는 자리. 대전이 아니면 null. `readShot()`이 이 값을 본다. */
let currentSide: LaunchSide | null = null;

/**
 * 다른 플레이어의 실시간 조준 (온라인 전용). 서버가 AimUpdate로 중계해준다.
 * 캔버스의 조준선과 HUD의 각도·세기 게이지가 이 값을 본다.
 * 턴이 바뀌거나 샷이 나가면 지운다 — 그 뒤로는 실제 샷의 조준선이 말해 준다.
 */
let liveAim: AimBroadcast | null = null;

function setLiveAim(aim: AimBroadcast | null): void {
  liveAim = aim;
  const show = liveAim !== null && !shotInFlight;
  el.oppAim.classList.toggle("hidden", !show);
  if (!liveAim) return;
  el.oppAim.classList.toggle("t0", liveAim.team !== 1);
  el.oppAim.classList.toggle("t1", liveAim.team === 1);
  // 2v2+에서는 같은 팀원의 조준도 중계된다 — 상대와 헷갈리지 않게 이름표를 바꾼다.
  el.oppAimLabel.textContent = liveAim.team === myTeam() ? "아군 조준" : "상대 조준";
  el.oppAngle.textContent = `${((liveAim.angle * 180) / Math.PI).toFixed(2)}°`;
  const k =
    (liveAim.power - SHOT_LIMITS.minPower) / (SHOT_LIMITS.maxPower - SHOT_LIMITS.minPower);
  el.oppPower.style.width = `${Math.round(Math.min(1, Math.max(0, k)) * 100)}%`;
}

// ── 로비 ────────────────────────────────────────────────
//
// 렌더러 기동(`await renderer.init`)은 **파일 맨 끝**에 있고, 로비는 여기서 먼저 세운다.
//
// Pixi의 app.init()은 GPU를 잡을 때까지 기다리는데, 백그라운드 탭처럼 화면을
// 안 그리는 상황에서는 영영 안 끝날 수 있다. 로비 배선을 그 뒤에 두면 초기화가
// 늦어지는 동안 모드 선택이 먹지 않고 AI 난이도 같은 필드가 계속 떠 있다.
// 로비는 캔버스와 아무 상관이 없으니 먼저 세운다.
//
// 대신 **시작 버튼은 잠가 둔다**. 캔버스가 없으면 판을 열어봐야 아무것도 안 보이고,
// 모듈이 await에서 멈춰 있는 동안 start()를 돌리면 아직 초기화 안 된 아래쪽
// 선언(로그 큐 등)을 건드려 TDZ로 죽는다. 실제로 그 사고가 났다.

/**
 * 초대 링크(?room=코드)로 들어왔는가.
 *
 * 이 값이 있으면 온라인 모드로 고정되고, 입장 버튼이 자동 매칭 대신 그 방으로
 * 곧장 들어간다 (joinById). 방이 사라졌으면 `clearInvite()`로 지우고 일반 흐름으로
 * 돌아간다. 초대받은 사람은 포맷을 고를 일이 없으므로 포맷 선택도 숨긴다.
 *
 * ⚠️ 아래 `syncLobbyFields()` 호출이 이 값을 읽으므로 그보다 먼저 선언해야 한다 (TDZ).
 */
let inviteCode: string | null =
  new URLSearchParams(location.search).get("room")?.trim().toUpperCase() || null;

el.status.textContent = "화면 준비 중…";
el.name.value = localStorage.getItem("scb.name") ?? "";
el.mode.value = localStorage.getItem("scb.mode") ?? "practice";
el.format.value = localStorage.getItem("scb.format") ?? "1v1";
el.difficulty.value = localStorage.getItem("scb.difficulty") ?? "normal";
el.knockout.checked = localStorage.getItem("scb.knockout") === "1";
// 초대받았으면 마지막에 쓰던 모드가 뭐였든 온라인이다 — 방이 이미 정해져 있다.
if (inviteCode) el.mode.value = "online";
syncLobbyFields();

/**
 * 허브로 돌아가는 링크.
 *
 * 허브 뒤(`/{slug}/`)에서는 한 단계 위가 곧 게임 목록이다. 단독 도메인
 * (scb.stonkradar.io)으로 직접 들어온 경우엔 올라갈 곳이 없으므로 허브 주소를
 * 그대로 쓴다 — HTML에 박아둔 기본값이 그것이라 손댈 필요가 없다.
 */
if (location.pathname.replace(/\/[^/]*$/, "") !== "") el.hubLink.href = "../";

el.mode.addEventListener("change", syncLobbyFields);
el.join.addEventListener("click", () => void start());
el.ready.addEventListener("click", () => session?.send(ClientMessage.Ready));
el.switchTeam.addEventListener("click", () => {
  const me = currentPlayer();
  if (!me) return;
  session?.send(ClientMessage.ChangeTeam, { team: me.team === 0 ? 1 : 0 });
});

function selectedMode(): SessionMode {
  const value = el.mode.value;
  return value === "online" || value === "ai" ? value : "practice";
}

/** 모드에 따라 필요 없는 입력을 숨긴다. */
function syncLobbyFields(): void {
  const mode = selectedMode();
  el.format.classList.toggle("hidden", mode !== "online" || inviteCode !== null);
  el.difficulty.classList.toggle("hidden", mode !== "ai");
  // 선택 규칙은 모든 모드에서 고를 수 있다. 초대받은 입장만 예외 —
  // 방을 만든 사람의 설정을 따르므로 골라봐야 의미가 없다.
  el.knockoutOpt.classList.toggle("hidden", inviteCode !== null);
  el.join.textContent =
    mode === "online"
      ? inviteCode
        ? `초대받은 방 입장 (${inviteCode})`
        : "입장"
      : mode === "ai"
        ? "AI와 대전"
        : "연습 시작";
}

/** 초대 상태를 접는다 — URL의 ?room=도 지워서 새로고침이 일반 입장이 되게 한다. */
function clearInvite(): void {
  if (inviteCode === null) return;
  inviteCode = null;
  const url = new URL(location.href);
  url.searchParams.delete("room");
  history.replaceState(null, "", url);
  syncLobbyFields();
}

// ── 친구 초대 ───────────────────────────────────────────
// 방 코드 클릭 = 초대 링크 복사. 링크를 받은 친구는 열기만 하면 이 방으로 들어온다.

const INVITE_HINT_DEFAULT = "누르면 초대 링크 복사";
let inviteHintTimer = 0;

/** 힌트 문구를 잠깐 바꿨다가 되돌린다 (복사 성공 피드백). */
function flashInviteHint(text: string): void {
  el.inviteHint.textContent = text;
  clearTimeout(inviteHintTimer);
  inviteHintTimer = window.setTimeout(() => {
    el.inviteHint.textContent = INVITE_HINT_DEFAULT;
  }, 2200);
}

/**
 * 클립보드 복사 — execCommand를 먼저 쓴다.
 *
 * async Clipboard API는 권한 프롬프트가 억제된 환경(webview, 일부 사파리)에서
 * resolve도 reject도 안 하고 **매달릴 수 있다**. 그러면 사용자는 아무 피드백을
 * 못 받는다. 구식 execCommand는 동기라 성공/실패가 그 자리에서 갈린다.
 */
function copyText(text: string): boolean {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  ta.remove();
  return ok;
}

el.invite.addEventListener("click", () => {
  const code = el.roomCode.textContent ?? "";
  if (!/^[A-Z0-9]+$/i.test(code)) return;
  const url = new URL(location.href);
  url.search = "";
  url.hash = "";
  url.searchParams.set("room", code);
  const link = url.toString();

  if (copyText(link)) {
    flashInviteHint("복사됨! 친구에게 보내세요");
    return;
  }
  // execCommand가 안 먹는 브라우저 → Clipboard API를 시도하되,
  // 그마저 실패하면 링크를 그대로 보여줘 손으로 복사하게 한다.
  if (navigator.clipboard) {
    navigator.clipboard.writeText(link).then(
      () => flashInviteHint("복사됨! 친구에게 보내세요"),
      () => {
        el.status.textContent = link;
      },
    );
  } else {
    el.status.textContent = link;
  }
});

// ── 채팅 (온라인 전용) ───────────────────────────────────
//
// 대기실에서는 로비 패널 안에 인라인으로, 경기 중에는 왼쪽 아래 접힌 버튼으로.
// 같은 #chatBox를 국면에 따라 옮겨 심는다 (applyState의 syncChatMount).
// 로컬 모드(연습·AI)는 서버가 없거나 채팅을 무시하므로 아예 안 보인다.

/** 팀 전용 전송 모드. 2v2 이상에서만 토글이 보인다 — 1v1의 팀 채팅은 혼잣말이다. */
let chatTeamOnly = false;
/** 접혀 있는 동안 도착한 메시지 수. 열면 0으로. */
let chatUnread = 0;
let chatPeekTimer = 0;

/** 채팅 로그에 남길 최대 줄 수 — 게임 한 판 분량으로 충분하다. */
const CHAT_LOG_LIMIT = 80;

function appendChat(msg: ChatBroadcast): void {
  const div = document.createElement("div");
  div.className = `m t${msg.team === 1 ? 1 : 0}`;
  const name = document.createElement("b");
  name.textContent = msg.name;
  div.append(name);
  if (msg.teamOnly) {
    const tag = document.createElement("i");
    tag.textContent = " [팀]";
    div.append(tag);
  }
  div.append(` ${msg.text}`);
  el.chatLog.append(div);
  while (el.chatLog.childElementCount > CHAT_LOG_LIMIT) el.chatLog.firstElementChild?.remove();
  el.chatLog.scrollTop = el.chatLog.scrollHeight;
}

function updateChatBadge(): void {
  el.chatBadge.textContent = chatUnread > 9 ? "9+" : String(chatUnread);
  el.chat.classList.toggle("unread", chatUnread > 0);
}

/** 접힌 채로 도착한 메시지를 버튼 위에 잠깐 띄운다 — 열지 않아도 흐름은 보인다. */
function showChatPeek(msg: ChatBroadcast): void {
  el.chatPeek.textContent = `${msg.name}: ${msg.text}`;
  el.chatPeek.classList.remove("hidden");
  clearTimeout(chatPeekTimer);
  chatPeekTimer = window.setTimeout(hideChatPeek, 4000);
}

function hideChatPeek(): void {
  clearTimeout(chatPeekTimer);
  chatPeekTimer = 0;
  el.chatPeek.classList.add("hidden");
}

function syncChatTeamUi(): void {
  el.chatTeam.textContent = chatTeamOnly ? "팀" : "전체";
  el.chatTeam.classList.toggle("team", chatTeamOnly);
}

/** 세션이 시작·종료될 때 채팅을 초기 상태로 되돌린다. */
function resetChat(): void {
  el.chatLog.replaceChildren();
  el.chatText.value = "";
  chatUnread = 0;
  updateChatBadge();
  hideChatPeek();
  el.chat.classList.remove("open");
  el.chat.classList.add("hidden");
  el.chatMountLobby.classList.add("hidden");
  if (el.chatBox.parentElement !== el.chatMountLobby) el.chatMountLobby.appendChild(el.chatBox);
  chatTeamOnly = false;
  el.chatTeam.classList.add("hidden");
  syncChatTeamUi();
}

/**
 * 채팅 UI를 국면에 맞춘다 — 대기실이면 로비 안 인라인, 경기 중이면 접힌 버튼.
 * applyState가 매 상태 변경마다 부르므로 멱등이어야 한다.
 */
function syncChatMount(state: BattleView, inMatch: boolean): void {
  if (session?.kind !== "online") return;
  const mount = inMatch ? el.chatPop : el.chatMountLobby;
  if (el.chatBox.parentElement !== mount) {
    mount.appendChild(el.chatBox);
    el.chatLog.scrollTop = el.chatLog.scrollHeight;
  }
  el.chat.classList.toggle("hidden", !inMatch);
  el.chatMountLobby.classList.toggle("hidden", inMatch);

  const mates = playersOf(state).filter((p) => p.team === myTeam()).length;
  const showTeamToggle = mates >= 2;
  el.chatTeam.classList.toggle("hidden", !showTeamToggle);
  if (!showTeamToggle && chatTeamOnly) {
    chatTeamOnly = false;
    syncChatTeamUi();
  }
}

el.chatToggle.addEventListener("click", () => {
  const open = el.chat.classList.toggle("open");
  if (!open) return;
  chatUnread = 0;
  updateChatBadge();
  hideChatPeek();
  el.chatLog.scrollTop = el.chatLog.scrollHeight;
  el.chatText.focus({ preventScroll: true });
});

el.chatTeam.addEventListener("click", () => {
  chatTeamOnly = !chatTeamOnly;
  syncChatTeamUi();
  el.chatText.focus();
});

el.chatForm.addEventListener("submit", (ev) => {
  ev.preventDefault();
  const text = el.chatText.value.trim();
  if (!text || session?.kind !== "online") return;
  // 서버가 200자에서 자르지만(handleChat) 입력창 maxlength가 먼저 막는다.
  session.send(ClientMessage.Chat, { text, teamOnly: chatTeamOnly });
  el.chatText.value = "";
});

// 입력 중 Escape — 채팅을 닫고 포커스를 놓아 키보드 조작(← → · Space)을 돌려준다.
el.chatText.addEventListener("keydown", (ev) => {
  if (ev.key !== "Escape") return;
  el.chatText.blur();
  el.chat.classList.remove("open");
});

async function start(): Promise<void> {
  const gen = ++generation;
  const mode = selectedMode();
  const name = el.name.value.trim() || "플레이어";
  const format = isMatchFormat(el.format.value) ? el.format.value : "1v1";
  const difficulty = el.difficulty.value as Difficulty;
  const knockoutPenalty = el.knockout.checked;

  localStorage.setItem("scb.name", name);
  localStorage.setItem("scb.mode", mode);
  localStorage.setItem("scb.format", format);
  localStorage.setItem("scb.difficulty", difficulty);
  localStorage.setItem("scb.knockout", knockoutPenalty ? "1" : "0");

  el.join.disabled = true;
  clearLog();
  resetChat();
  setShotInFlight(false);
  setLiveAim(null);

  try {
    if (mode === "online") {
      el.status.textContent = inviteCode ? "초대받은 방을 찾는 중…" : "접속 중…";
      // ballsPerTeam을 명시해야 3D 클라이언트(같은 옵션을 보낸다)와 같은 방에 매칭된다.
      const remote = await createRemoteSession(
        { name, format, ballsPerTeam: 12, knockoutPenalty },
        inviteCode ? { kind: "code", code: inviteCode } : { kind: "quick" },
      );
      if (gen !== generation) {
        // 접속을 기다리는 동안 사용자가 다른 모드를 시작했다.
        await remote.leave();
        return;
      }
      session = remote;
      exposeForDebug(remote);
      el.status.textContent = inviteCode
        ? "초대받은 방 입장 — 다른 플레이어를 기다립니다"
        : `${MATCH_FORMATS[format].label} 방 입장 — 다른 플레이어를 기다립니다`;
      // 방 코드를 크게 보여준다 — 누르면 초대 링크가 복사된다.
      el.roomCode.textContent = remote.roomId;
      el.inviteHint.textContent = INVITE_HINT_DEFAULT;
      el.invite.classList.remove("hidden");
      // 대기실 채팅 — 첫 상태 동기화 전에도 입력할 수 있게 즉시 연다.
      el.chatMountLobby.classList.remove("hidden");
      el.ready.classList.remove("hidden");
      el.switchTeam.classList.toggle("hidden", MATCH_FORMATS[format].teamSize === 1);
      el.hud.classList.remove("hidden");
      el.rules.classList.remove("hidden");
      wireSession(remote);
      return;
    }

    // 초대 링크로 들어왔지만 다른 모드를 골랐다 — 초대는 여기서 끝난 것으로 본다.
    clearInvite();

    // 로컬 모드는 대기 없이 바로 시작한다.
    const local = new LocalSession({
      mode,
      name,
      difficulty,
      knockoutPenalty,
      // 시드가 매번 달라야 같은 판이 반복되지 않는다.
      seed: (Date.now() ^ Math.floor(performance.now() * 1000)) | 0,
    });
    session = local;
    exposeForDebug(local);
    wireSession(local);
    local.onMessage<PracticeSummary>(LOCAL_PRACTICE_SUMMARY, (summary) => {
      const average = summary.shots > 0 ? (summary.total / summary.shots).toFixed(2) : "0.00";
      log(
        `세트 종료 — ${summary.shots}구 ${summary.total}점 (평균 ${average}, 최고 ${summary.best}점)`,
      );
      updatePracticeStat();
    });
    el.practice.classList.toggle("hidden", mode !== "practice");
    el.hud.classList.remove("hidden");
    el.rules.classList.remove("hidden");
    local.start();
    if (mode === "practice") {
      log(`연습 시작 — ${PRACTICE_BALLS}구. 각도·세기·스핀을 바꿔가며 던져보세요.`);
    } else {
      log(`AI 대전 시작 — 난이도 ${difficulty}. 2라운드 × 6분.`);
    }
  } catch (err) {
    if (mode === "online" && inviteCode) {
      // 초대받은 방이 사라졌다 (이미 시작됐거나 방장이 나감). 일반 입장으로 되돌린다.
      el.status.textContent =
        "초대받은 방을 찾을 수 없습니다 — 이미 시작됐거나 닫힌 방입니다. 다시 누르면 자동 매칭으로 입장합니다";
      clearInvite();
    } else {
      el.status.textContent = `시작 실패: ${err instanceof Error ? err.message : String(err)}`;
    }
    el.join.disabled = false;
  }
}

async function quit(): Promise<void> {
  const gen = ++generation;
  const previous = session;
  // 타이머·AI 예약을 즉시 끊는다 (leave()의 정리 부분은 동기로 실행된다).
  session = null;
  setShotInFlight(false);
  await previous?.leave();
  // 정리를 기다리는 동안 새 세션이 시작됐으면 UI를 건드리지 않는다.
  if (gen !== generation) return;

  renderer.setBalls([]);
  renderer.clearAim();
  setLiveAim(null);
  updateTurnClock();
  syncSeat(null);
  el.hud.classList.add("hidden");
  el.rules.classList.add("hidden");
  el.controls.classList.add("hidden");
  el.practice.classList.add("hidden");
  el.finalSequence.classList.add("hidden");
  el.ready.classList.add("hidden");
  el.switchTeam.classList.add("hidden");
  el.invite.classList.add("hidden");
  resetChat();
  el.roster.replaceChildren();
  el.status.textContent = "";
  el.lobby.classList.remove("hidden");
  el.join.disabled = false;
  // 패널이 전부 사라졌으니 캔버스가 화면 전체를 되찾는다 (applyState 참고).
  measureChrome();
}

el.quit.addEventListener("click", () => void quit());
el.swapSeat.addEventListener("click", () => {
  if (!(session instanceof LocalSession) || session.kind !== "practice") return;
  const side = session.swapPracticeSide();
  // sync()가 상태 변경을 흘려보내 applyState → syncSeat까지 이어지지만,
  // 자리 좌표를 즉시 반영해야 조준선이 한 프레임도 엉뚱한 곳에 남지 않는다.
  syncSeat(side);
  log(`자리 변경 — ${sideLabel(side)}`);
});

el.reset.addEventListener("click", () => {
  if (session instanceof LocalSession) {
    session.reset();
    clearLog();
    setShotInFlight(false);
    log(`새 세트 — ${PRACTICE_BALLS}구`);
  }
});

/** 개발 편의: 콘솔에서 window.__scb.send("shoot", {...}) 로 직접 조작 가능. */
function exposeForDebug(s: GameSession): void {
  if (import.meta.env.DEV) Object.assign(window, { __scb: s, __scbRenderer: renderer });
}

function currentPlayer() {
  if (!session) return null;
  const id = session.sessionId;
  return playersOf(session.state).find((p) => p.sessionId === id) ?? null;
}

// ── 세션 이벤트 ─────────────────────────────────────────

function wireSession(s: GameSession): void {
  // HUD·로스터는 항상 즉시 반영한다. 공 위치만 리플레이가 끝난 뒤에 반영되고,
  // 그 지연은 renderer.setBalls()가 내부적으로 처리한다.
  s.onStateChange((state) => applyState(state));

  s.onMessage<ShotResultPayload>(ServerMessage.ShotResult, (msg) => {
    // 상대(AI·온라인) 샷도 재생 내내 결과를 감춰야 하므로 여기서도 잠근다.
    // playReplay가 프레임 0개일 때 콜백을 동기로 부르므로 반드시 그 **전에** 세운다.
    setShotInFlight(true);
    syncShootButton();
    // 조준은 끝났다 — 이제부터는 실제 샷의 조준선이 그 자리를 대신한다.
    setLiveAim(null);

    const where = msg.score.balls
      .map((b) => `${b.astrolabe}번판 ${slotLabel(SLOT_COUNTS[b.astrolabe], b.slot)}칸`)
      .join(", ");
    const summary =
      msg.score.total > 0
        ? `${msg.score.total}점 (${where})` +
          (msg.score.grandSlams.length > 0 ? ` · 만관 ${msg.score.grandSlams.join(",")}번판` : "")
        : reasonLabel(msg.turnEndReason);
    const who = s.kind === "practice" ? "" : `${teamLabel(msg.by.team)} `;
    const line = `${who}${msg.totals[0]} : ${msg.totals[1]} — ${summary}`;

    // 빠져나간 공 알림. 지금 log()하면 재생 중이라 밀려 있다가(pendingLog)
    // 위 결과 줄 다음에 순서대로 나온다.
    for (const k of msg.knockouts ?? []) {
      const penalty = k.penalty > 0 ? ` (−${k.penalty}점)` : "";
      log(`${teamLabel(k.team)} 공이 ${k.astrolabe}번판에서 빠짐${penalty}`);
    }

    renderer.playReplay(msg.replay.frames, msg.replay.sampleHz, msg.by.team, () => {
      renderer.clearAim();
      // 결과는 공이 다 굴러간 뒤에 알려준다. 여기서 먼저 log()하면
      // 리플레이 중에 밀려 있던 로그(라운드 종료 등)보다 앞에 놓인다.
      setShotInFlight(false);
      log(line);
      flushLog();
      // 재생이 끝나면 권위 상태로 스냅한다.
      if (session) applyState(session.state);
    });

    // 재생 내내 **쏜 사람의 조준선**을 띄워둔다. 상대가 어느 자리에서 몇 도로
    // 쐈는지 그대로 보인다 — 자리가 고정이라 각도가 곧 그 사람의 수(手)다.
    // playReplay가 시작할 때 clearAim()을 하므로 반드시 그 **뒤에** 그린다.
    if (s.kind !== "practice") {
      renderer.showAim([
        {
          x: msg.input.x,
          y: DEFAULT_TABLE.playLineY,
          team: msg.by.team,
          angle: msg.input.angle,
          own: msg.by.team === myTeam(),
        },
      ]);
    }
  });

  s.onMessage<AimBroadcast>(ServerMessage.AimUpdate, (msg) => {
    // 서버가 보낸 사람은 빼고 중계하지만, 미래의 실수에 대비해 한 번 더 거른다.
    if (msg.sessionId === s.sessionId) return;
    setLiveAim(msg);
    // 재생 중에는 쏜 샷의 조준선이 떠 있어야 한다 (updateAim의 가드와 같은 이유).
    if (!shotInFlight) updateAim();
  });

  s.onMessage<TurnChangedPayload>(ServerMessage.TurnChanged, (msg) => {
    el.finalSequence.classList.add("hidden");
    setLiveAim(null);
    // 자리는 **메시지의 라운드**로 맞춘다. 상태 패치는 주기적으로 오므로
    // 라운드 전환 때 이 메시지가 먼저 도착할 수 있고, 그때 state.round를 믿으면
    // 이전 라운드 자리로 쏴서 서버가 파울로 거부한다 (1v1 스모크 테스트에서 재현).
    if (s.kind !== "practice") {
      const me = currentPlayer();
      if (me) syncSeat(launchSideFor(msg.round, me.team === 1 ? 1 : 0));
    }
    updateTurnUi(msg.sessionId === s.sessionId);
  });

  s.onMessage<FinalSequenceOfferPayload>(ServerMessage.FinalSequenceOffer, (msg) => {
    const me = currentPlayer();
    if (!me || me.team !== msg.team) return;
    el.fsText.textContent = `상대 공 소진 — 남은 ${msg.ballsRemaining}개를 ${msg.bankValue}점으로 환산하거나 계속 진행`;
    el.finalSequence.classList.remove("hidden");
  });

  s.onMessage<RoundEndedPayload>(ServerMessage.RoundEnded, (msg) => {
    setLiveAim(null);
    if (msg.reason === "grand_slam" && msg.grandSlam) {
      log(
        `만관! ${teamLabel(msg.grandSlam.team)}이 ${msg.grandSlam.astrolabe}번판을 다 채웠다` +
          ` — 라운드 ${msg.round} 즉시 종료`,
      );
    }
    log(
      `라운드 ${msg.round} 종료 (${roundEndLabel(msg.reason)}) — ${msg.totals[0]} : ${msg.totals[1]}` +
        ` (잔여공 보너스 +${msg.leftoverBonus[0]} / +${msg.leftoverBonus[1]})`,
    );
    if (msg.nextPhase === "intermission") log("자리를 교대합니다 — 다음 라운드 준비");
    if (msg.nextPhase === "overtime") log("동점 — 연장전 (먼저 득점하면 승리)");
  });

  s.onMessage<MatchEndedPayload>(ServerMessage.MatchEnded, (msg) => {
    setLiveAim(null);
    const label = msg.winner === null || msg.winner < 0 ? "무승부" : `${teamLabel(msg.winner)} 승리`;
    log(`경기 종료 — ${label} (${msg.totals[0]} : ${msg.totals[1]})`);
    el.finalSequence.classList.add("hidden");
    if (s.kind !== "online") el.practice.classList.remove("hidden");
  });

  s.onMessage<ChatBroadcast>(ServerMessage.Chat, (msg) => {
    if (s.kind !== "online") return;
    appendChat(msg);
    // 경기 중 접혀 있으면(로비 인라인은 늘 보인다) 안읽음 배지 + 미리보기.
    const collapsed =
      el.chatBox.parentElement === el.chatPop && !el.chat.classList.contains("open");
    if (collapsed && msg.sessionId !== s.sessionId) {
      chatUnread += 1;
      updateChatBadge();
      showChatPeek(msg);
    }
  });

  s.onMessage<RejectedPayload>(ServerMessage.Rejected, (msg) => {
    // 거부되면 ShotResult가 안 오므로 여기서 잠금을 풀어야 버튼이 되살아난다.
    setShotInFlight(false);
    syncShootButton();
    log(`거부됨: ${msg.code}${msg.detail ? ` — ${msg.detail}` : ""}`);
  });
}

function applyState(state: BattleView): void {
  const teams = state.teams ?? [];
  // 점수는 공이 다 굴러간 뒤에 올린다. 재생 중에 올리면 결과가 미리 새어 나간다.
  // 재생이 끝나면 리플레이 콜백이 권위 상태로 다시 applyState를 부른다.
  if (!shotInFlight) {
    el.score0.textContent = String(teams[0]?.score ?? 0);
    el.score1.textContent = String(teams[1]?.score ?? 0);
  }
  // 남은 공은 바로 줄인다 — 이미 굴러가고 있는 공이라 결과를 흘리지 않는다.
  el.balls0.textContent = `공 ${teams[0]?.ballsRemaining ?? 0}`;
  el.balls1.textContent = `공 ${teams[1]?.ballsRemaining ?? 0}`;
  el.phase.textContent = phaseLabel(state);

  // 연습 모드는 상대가 없으니 상대편 표시를 숨긴다.
  const solo = session?.kind === "practice";
  el.score1.classList.toggle("hidden", solo);
  el.balls1.classList.toggle("hidden", solo);

  renderer.setBalls([...(state.balls ?? [])]);
  renderRoster(state);
  updatePracticeStat();
  syncSeat(mySide(state));

  const inMatch = state.phase !== "lobby" && state.phase !== "countdown";
  el.lobby.classList.toggle("hidden", inMatch);
  syncChatMount(state, inMatch);

  // 조준 패널은 경기 중 내내 띄워둔다 (상대 차례·재생 중에도 미리 맞춰볼 수 있게).
  // 실제로 쏠 수 있는지는 발사 버튼의 disabled로만 표현한다.
  el.controls.classList.toggle("hidden", !inMatch);

  // "ended"/"intermission"에서는 슛을 받지 않으므로 버튼을 잠근다.
  const canShoot = state.phase === "round" || state.phase === "overtime";
  updateTurnUi(state.currentShooter === session?.sessionId && canShoot);

  // 패널을 숨기고 드러낸 **뒤에** 잰다. 위의 syncSeat()도 재지만 그건 이 토글들보다
  // 먼저라, 거기서만 재면 경기 첫 진입에 하단 독 높이가 0으로 남아 캔버스가
  // 독 밑으로 깔린다. ResizeObserver가 결국 따라잡긴 하지만 백그라운드 탭에서는
  // 렌더링 스텝이 멈춰서 콜백이 오지 않는다 — 순서를 맞추는 쪽이 확실하다.
  measureChrome();
}

function updatePracticeStat(): void {
  if (!(session instanceof LocalSession) || session.kind !== "practice") return;
  // 점수와 같은 이유로 굴러가는 중에는 갱신하지 않는다 (applyState 참고).
  if (shotInFlight) return;
  const { shots, total, best } = session.practiceStats;
  const average = shots > 0 ? (total / shots).toFixed(2) : "0.00";
  el.practiceStat.textContent = `${shots}구 · ${total}점 · 평균 ${average} · 최고 ${best}`;
}

function renderRoster(state: BattleView): void {
  const players = playersOf(state);
  el.roster.replaceChildren(
    ...players.map((p) => {
      const div = document.createElement("div");
      div.className = `p${p.ready ? " ready" : ""}`;
      div.textContent = `${teamLabel(p.team)} #${p.slot + 1}  ${p.name}${p.connected ? "" : " (연결 끊김)"}`;
      const tag = document.createElement("span");
      tag.textContent = p.ready ? "준비" : "대기";
      div.appendChild(tag);
      return div;
    }),
  );
}

function updateTurnUi(turn: boolean): void {
  const wasMyTurn = myTurn;
  myTurn = turn;
  if (session?.kind === "practice") {
    el.turn.textContent = shotInFlight ? "굴러가는 중…" : turn ? "던지세요" : "";
  } else {
    el.turn.textContent = turn ? "내 차례" : "상대 차례";
  }
  // "내 차례"만 강조색으로 남긴다 — 상대 차례는 흐리게, 대신 카운트다운이 말해 준다.
  el.turn.classList.toggle("theirs", session?.kind !== "practice" && !turn);
  updateTurnClock();
  // 내 턴이 새로 시작됐다 — 마지막으로 보낸 조준값을 잊어서, 슬라이더를 안
  // 건드려도 아래 updateAim()이 현재 조준을 한 번 중계하게 한다.
  if (turn && !wasMyTurn) aimSent = { angle: NaN, power: NaN };
  syncShootButton();
  updateAim();
}

/**
 * 이번 턴의 남은 시간 칩.
 *
 * 페이즈 줄의 "라운드 1 · 5:32"는 라운드 전체 시계라서, 20초짜리 턴 제한이
 * 얼마 남았는지는 이 칩이 유일한 표시다. 값은 동기화된 `state.turnEndsAt`에서
 * 읽으므로 서버 판정(시간 초과)과 어긋나지 않는다.
 *
 * 연습·AI 모드는 turnEndsAt이 0으로 남아 자연히 숨겨진다.
 */
function updateTurnClock(): void {
  const state = session?.state;
  const active =
    session !== null &&
    session.kind !== "practice" &&
    state !== undefined &&
    (state.phase === "round" || state.phase === "overtime") &&
    state.turnEndsAt > 0;
  el.turnClock.classList.toggle("hidden", !active);
  if (!active || !state) return;

  const remain = Math.max(0, Math.ceil((state.turnEndsAt - Date.now()) / 1000));
  el.turnClock.textContent = `${remain}초`;
  el.turnClock.classList.toggle("urgent", remain <= 5);
}

/**
 * 지금 조준·발사가 유효한 상태인가.
 *
 * `shotInFlight`를 보는 이유: 상대(AI)가 빗맞혀서 턴이 곧바로 나에게 돌아오면
 * 상대 공이 아직 굴러가는 중인데 내 턴 상태가 먼저 도착한다. 그때 쏘면
 * 재생이 잘리고 결과 순서가 꼬인다.
 */
function canShootNow(): boolean {
  return myTurn && !shotInFlight;
}

function syncShootButton(): void {
  const ready = canShootNow();
  el.shoot.disabled = !ready;
  // 조그는 disabled를 못 다니까 컨테이너에 표시한다 — 만져도 안 돈다는 걸 보여준다.
  el.controls.classList.toggle("locked", !ready);
  // 턴이 넘어갔는데 게이지나 각도가 계속 돌면 다음 턴 값이 멋대로 정해진다.
  if (!ready) {
    stopPowerCharge();
    stopAngleHold();
    stopJog();
  }
}

// ── 자리 (좌/우 고정) ───────────────────────────────────

/**
 * 이번 라운드에 내가 쓰는 자리. 대전이 아니거나 아직 라운드가 안 시작했으면 null.
 *
 * 자리는 라운드와 팀에서 순수 함수로 유도된다 — 서버가 쓰는 것과 **같은**
 * `launchSideFor`라 서버 판정과 어긋날 수 없다.
 *
 * 연습 모드를 제외로 두는 건 `LocalSession`이 연습 설정에서 `fixedLaunchSides`를
 * 끄기 때문이다. 클라이언트는 서버의 RulesConfig를 못 보므로 모드로 판단한다.
 */
function mySide(state: BattleView): LaunchSide | null {
  if (!session) return null;
  // 연습도 자리에 앉아서 던진다. 라운드 교대가 없어 세션이 직접 자리를 들고 있다.
  if (session.kind === "practice") {
    return session instanceof LocalSession ? session.practiceSide : null;
  }
  if (state.round < 1) return null;
  const me = currentPlayer();
  if (!me) return null;
  return launchSideFor(state.round, me.team === 1 ? 1 : 0);
}

/**
 * 대전에서 내가 쏘는 고정 지점. 연습(자리 없음)이면 슬라이더 값을 그대로 쓴다.
 * `readShot()`이 이 값을 보내므로 슬라이더를 조작해도 대전에서는 영향이 없다.
 */
function launchX(side: LaunchSide | null): number {
  return side === null ? Number(el.pos.value) : launchPointFor(DEFAULT_TABLE, side);
}

/**
 * 자리 UI를 현재 라운드에 맞춘다.
 *
 * 대전은 **위치를 못 바꾼다** — 슬라이더를 잠그고 자리 좌표를 그대로 보여준다.
 * 각도·세기·스핀만으로 세 판을 다 노릴 수 있는 자리라서(`launchPointFor` 참고)
 * 위치를 잠가도 전략이 좁아지지 않는다.
 */
function syncSeat(side: LaunchSide | null): void {
  currentSide = side;
  // 컴팩트 모드는 세로 공간이 아까우므로, 못 만지는 슬라이더는 아예 치운다.
  // 자리가 고정이라는 사실은 HUD의 "자리: 왼쪽 (고정)"이 대신 말해 준다.
  el.posCtl.classList.toggle("locked", side !== null);
  measureChrome();

  if (side === null) {
    el.pos.disabled = false;
    el.pos.min = FREE_MIN_X.toFixed(2);
    el.pos.max = FREE_MAX_X.toFixed(2);
    el.posLabel.textContent = "위치";
    el.seat.textContent = "";
    renderer.setSeat(null);
  } else {
    const x = launchPointFor(DEFAULT_TABLE, side);
    el.pos.disabled = true;
    // 잠긴 슬라이더도 눈금이 자리에 맞아야 한다. 범위를 그 점 하나로 좁힌다.
    el.pos.min = x.toFixed(2);
    el.pos.max = x.toFixed(2);
    el.pos.value = x.toFixed(2);
    el.posLabel.textContent = "위치 (고정)";
    el.seat.textContent = `자리: ${sideLabel(side)} (고정)`;
    renderer.setSeat(x);
  }

  updateAim();
}

function sideLabel(side: LaunchSide): string {
  return side === "left" ? "왼쪽" : "오른쪽";
}

// ── 조준 / 발사 ─────────────────────────────────────────

function readShot(): ShotInput {
  return {
    // 대전에서는 슬라이더가 아니라 자리 좌표를 보낸다 — 위치는 못 바꾼다.
    x: launchX(currentSide),
    angle: (Number(el.angle.value) * Math.PI) / 180,
    power: Number(el.power.value),
    spin: Number(el.spin.value),
  };
}

// ── 조준 중계 (온라인) ──
// 내 차례에 각도·세기를 만질 때마다 서버로 흘려서 상대 화면에도 내 조준선이
// 실시간으로 보이게 한다. 자리가 고정이라 어차피 공개될 정보를 미리 보여주는
// 것뿐이고, 상대가 내 수를 지켜보는 재미가 대전의 일부다.

/** 전송 주기 (ms). 10Hz — 조준선 표시용으로는 충분하고 대역폭 부담이 없다. */
const AIM_SEND_MS = 100;
let aimSendTimer = 0;
let aimSentAt = 0;
/** 마지막으로 보낸 값. 안 바뀌었으면 안 보낸다. 내 턴이 시작되면 리셋된다. */
let aimSent = { angle: NaN, power: NaN };

function sendAimSoon(): void {
  if (session?.kind !== "online" || !canShootNow()) return;
  const angle = (Number(el.angle.value) * Math.PI) / 180;
  const power = Number(el.power.value);
  if (angle === aimSent.angle && power === aimSent.power) return;

  const elapsed = performance.now() - aimSentAt;
  if (elapsed >= AIM_SEND_MS) {
    aimSentAt = performance.now();
    aimSent = { angle, power };
    session.send(ClientMessage.Aim, { angle, power });
    return;
  }
  // 주기 안에 또 바뀌었다 — 주기가 차면 **마지막 값**을 한 번 보낸다.
  // 이게 없으면 조준을 멈춘 최종 각도가 상대에게 안 남을 수 있다.
  if (aimSendTimer === 0) {
    aimSendTimer = window.setTimeout(() => {
      aimSendTimer = 0;
      sendAimSoon();
    }, AIM_SEND_MS - elapsed);
  }
}

function updateAim(): void {
  const shot = readShot();
  el.angleOut.textContent = `${Number(el.angle.value).toFixed(2)}°`;
  el.powerOut.textContent = Number(el.power.value).toFixed(2);
  el.spinOut.textContent = Number(el.spin.value).toFixed(1);
  el.posOut.textContent = shot.x.toFixed(2);
  sendAimSoon();
  // 눈금은 **손가락과 같은 방향으로** 흐른다. 눈금에 숫자가 없으므로 이건 눈금을
  // 읽는 자가 아니라 "얼마나 돌렸는지"를 보여주는 조그 다이얼이다 — 손가락을 따라
  // 오는 쪽이 맞다. 값은 위의 큰 숫자가 읽어준다.
  el.jogTicks.style.setProperty("--jog-offset", `${Number(el.angle.value) * JOG_TICK_PX}px`);

  // 공이 굴러가는 중에는 그 샷의 조준선이 떠 있다 (ShotResult 핸들러가 그린다).
  // 여기서 덮어쓰면 상대가 어떻게 쐈는지 볼 수 없게 된다.
  if (shotInFlight) return;

  renderer.showAim(aimViews(shot.angle));
}

/**
 * 지금 그려야 할 자리 공들.
 *
 * 대전이면 **양 팀 공을 항상** 놓는다 — 자리가 고정이라 위치는 공개 정보이고,
 * 상대가 안 보이면 대전이라는 느낌이 안 난다. 조준선은 내가 쏠 수 있을 때
 * 내 것을 그리고, 다른 플레이어가 조준 중이면 서버가 중계해준 각도(`liveAim`)로
 * 그쪽 자리에도 그린다 — 상대가 어디를 겨누는지 실시간으로 보인다.
 */
function aimViews(myAngle: number): AimView[] {
  const state = session?.state;
  if (!session || !state) return [];

  // 연습은 상대가 없다 — 내 공만, 그것도 겨누는 동안만 그린다.
  if (session.kind === "practice") {
    if (!canShootNow()) return [];
    return [
      {
        x: launchX(currentSide),
        y: DEFAULT_TABLE.playLineY,
        team: myTeam(),
        angle: myAngle,
        own: true,
      },
    ];
  }

  if (state.phase !== "round" && state.phase !== "overtime") return [];

  const mine = myTeam();
  return ([0, 1] as const).map((team) => {
    // 내가 조준 중이면 내 각도, 아니면 그 팀에서 중계된 각도 (없으면 공만).
    const angle =
      team === mine && canShootNow()
        ? myAngle
        : liveAim !== null && liveAim.team === team
          ? liveAim.angle
          : null;
    return {
      x: launchPointFor(DEFAULT_TABLE, launchSideFor(state.round, team)),
      y: DEFAULT_TABLE.playLineY,
      team,
      angle,
      own: team === mine,
    };
  });
}

function myTeam(): 0 | 1 {
  return currentPlayer()?.team === 1 ? 1 : 0;
}

for (const input of [el.angle, el.power, el.spin, el.pos]) {
  input.addEventListener("input", updateAim);
}

// 테이블 클릭으로는 각도를 잡지 않는다. 마우스로 찍으면 화면 픽셀 해상도만큼만
// 정밀해서(1px ≈ 0.3°) 칸 여유 안에 넣기 어렵고, 키보드 조작과 값이 서로 튄다.
// 조준은 화살표 키(정밀)로 통일한다.

function fire(): void {
  if (!session || !canShootNow()) return;
  retireTouchHint();
  stopPowerCharge();
  const shot = readShot();
  // send()가 동기로 Rejected를 던질 수 있으므로 잠금을 **먼저** 건다.
  // 그래야 Rejected 핸들러가 푼 잠금을 여기서 다시 걸어버리지 않는다.
  setShotInFlight(true);
  syncShootButton();
  session.send(ClientMessage.Shoot, shot);
}

// 발사 버튼의 click 핸들러는 아래 "터치 조작"에 있다 — 컴팩트 모드에서 홀드로
// 이미 쏜 경우를 걸러내야 해서, 두 경로가 한 자리에 있어야 읽힌다.

// ── 키보드 조작 ─────────────────────────────────────────
//
// 대전은 자리가 고정이라 조절할 게 각도·세기·스핀뿐이다. 슬라이더를 마우스로
// 집는 것보다 키보드가 빠르고 정확해서 기본 조작으로 둔다.
//
//   ← →         각도 — 톡 누르면 0.25°, 누르고 있으면 점점 빨라진다
//   Space       누르면 세기 게이지가 왕복, **떼는 순간 발사**
//   Enter       발사 (세기를 슬라이더로 맞췄을 때)

/**
 * 한 번 톡 누를 때 움직이는 각도. 슬라이더 step과 같다 —
 * 칸 여유가 ±13mm라 1° 단위로는 조준이 안 된다.
 */
const ANGLE_TAP = 0.25;
/** Shift와 함께 톡 누르면 이만큼 — 반대편 판으로 크게 옮길 때. */
const ANGLE_TAP_COARSE = 2;

/**
 * 누르고 있을 때의 회전 속도 (°/초). 시작은 느리고 점점 빨라진다.
 *
 * 고정 속도로 두면 정밀 조준(0.25° 단위)과 반대편으로 훑기(120° 이동) 중
 * 하나는 반드시 불편해진다. 탭은 정확하게, 홀드는 빠르게 가져가는 이유다.
 */
const ANGLE_SPEED_MIN = 22;
const ANGLE_SPEED_MAX = 120;
/** 최고 속도까지 가속되는 시간 (ms). */
const ANGLE_RAMP_MS = 650;
/**
 * 이 시간까지는 홀드로 치지 않는다. 톡 누른 게 0.25° 정확히 한 눈금이 되도록
 * OS 키 반복처럼 짧은 유예를 둔다.
 */
const ANGLE_HOLD_DELAY_MS = 200;

/**
 * 진행 중인 홀드. `token`으로 세대를 구분한다 —
 * 이전 홀드의 rAF 콜백이 새 홀드가 시작된 뒤에 한 번 더 실행되면, 자기 것이
 * 아닌 `angleHold`에 다음 프레임을 걸어버려 루프가 둘로 갈라진다.
 * 그러면 각도가 두 배 속도로 돌고, 멈춰도 하나가 살아남는다.
 */
let angleHold: { dir: -1 | 1; raf: number; token: number } | null = null;
let angleHoldToken = 0;

/**
 * 화살표를 누르고 있는 동안 각도를 연속으로 돌린다.
 *
 * 내부 실수값(`acc`)을 따로 들고 간다. range input은 step(0.25)에 스냅되므로
 * 입력값을 매 프레임 다시 읽으면 반올림 오차가 쌓여 속도가 어긋난다.
 */
function startAngleHold(dir: -1 | 1): void {
  stopAngleHold();

  const min = Number(el.angle.min);
  const max = Number(el.angle.max);
  const startedAt = performance.now();
  const token = ++angleHoldToken;
  let acc = Number(el.angle.value);
  let last = startedAt;

  const step = (now: number) => {
    // 내 세대가 아니면 이미 끝난 홀드다. 조용히 사라진다.
    if (angleHold?.token !== token) return;

    const held = now - startedAt;
    if (held >= ANGLE_HOLD_DELAY_MS) {
      const ramp = Math.min(1, (held - ANGLE_HOLD_DELAY_MS) / ANGLE_RAMP_MS);
      const speed = ANGLE_SPEED_MIN + (ANGLE_SPEED_MAX - ANGLE_SPEED_MIN) * ramp;
      acc = Math.min(max, Math.max(min, acc + (dir * speed * (now - last)) / 1000));
      el.angle.value = acc.toFixed(2);
      updateAim();
    }
    last = now;
    angleHold.raf = requestAnimationFrame(step);
  };

  angleHold = { dir, raf: requestAnimationFrame(step), token };
}

function stopAngleHold(): void {
  if (angleHold === null) return;
  cancelAnimationFrame(angleHold.raf);
  angleHold = null;
}

/** 세기 게이지가 최소 → 최대를 한 번 훑는 시간 (ms). */
const POWER_SWEEP_MS = 1200;

let powerCharge: number | null = null;

/**
 * 세기 게이지를 최소↔최대로 왕복시킨다. 떼는 순간의 값이 그 샷의 세기다.
 *
 * 항상 **최소에서 새로 시작**한다. 직전 값에서 이어 가면 원하는 세기 근처에서
 * 짧게 눌러 정확히 맞출 수 있어서 게이지를 둔 의미가 사라진다.
 */
function startPowerCharge(): void {
  if (powerCharge !== null) return;
  const min = Number(el.power.min);
  const max = Number(el.power.max);
  const startedAt = performance.now();

  const apply = (now: number) => {
    const phase = ((now - startedAt) % (POWER_SWEEP_MS * 2)) / POWER_SWEEP_MS;
    const k = phase <= 1 ? phase : 2 - phase; // 0 → 1 → 0
    el.power.value = (min + k * (max - min)).toFixed(2);
    // 발사 버튼이 곧 게이지다 (컴팩트 모드에서만 그려진다).
    el.shoot.style.setProperty("--charge", k.toFixed(3));
    updateAim();
  };

  el.shoot.classList.add("charging");

  // 첫 프레임을 기다리지 않고 즉시 최소치로 내린다. 안 그러면 누른 순간 한 프레임
  // 동안 직전 세기가 남아 있어서 게이지가 어디서 출발하는지 헷갈린다.
  apply(startedAt);

  const step = (now: number) => {
    apply(now);
    powerCharge = requestAnimationFrame(step);
  };
  powerCharge = requestAnimationFrame(step);
}

function stopPowerCharge(): void {
  // 클래스는 언제 불려도 떨어뜨린다 — 턴이 넘어가 잠길 때도 여기를 지난다.
  el.shoot.classList.remove("charging");
  if (powerCharge === null) return;
  cancelAnimationFrame(powerCharge);
  powerCharge = null;
}

/** 로비 입력창에 타이핑 중이면 게임 단축키가 가로채면 안 된다. */
function isTypingTarget(target: EventTarget | null): boolean {
  const node = target as HTMLElement | null;
  if (!node) return false;
  if (node.tagName === "SELECT" || node.tagName === "TEXTAREA") return true;
  return node.tagName === "INPUT" && (node as HTMLInputElement).type !== "range";
}

function nudgeAngle(direction: -1 | 1, degrees: number): void {
  const min = Number(el.angle.min);
  const max = Number(el.angle.max);
  const next = Number(el.angle.value) + direction * degrees;
  el.angle.value = Math.min(max, Math.max(min, next)).toFixed(2);
  updateAim();
}

window.addEventListener("keydown", (ev) => {
  if (isTypingTarget(ev.target) || ev.metaKey || ev.ctrlKey || ev.altKey) return;
  if (!canShootNow()) return;

  const arrow = ev.code === "ArrowLeft" ? -1 : ev.code === "ArrowRight" ? 1 : 0;
  if (arrow !== 0) {
    ev.preventDefault();
    // OS 키 반복은 무시한다. 우리가 rAF으로 직접 굴리는 게 훨씬 부드럽고,
    // 둘을 같이 두면 속도가 두 배로 겹친다.
    if (ev.repeat) return;
    // 톡 누른 것만으로도 한 눈금은 움직인다. 계속 누르고 있으면
    // ANGLE_HOLD_DELAY_MS 뒤부터 startAngleHold가 이어받아 가속한다.
    nudgeAngle(arrow, ev.shiftKey ? ANGLE_TAP_COARSE : ANGLE_TAP);
    if (!ev.shiftKey) startAngleHold(arrow);
    return;
  }

  switch (ev.code) {
    case "Space":
      // 기본 동작(스크롤 / 포커스된 버튼 누르기)을 막아야 게이지만 돈다.
      ev.preventDefault();
      // 꾹 누르고 있으면 keydown이 반복해서 오는데, 게이지는 한 번만 시작한다.
      if (!ev.repeat) startPowerCharge();
      break;
    case "Enter":
      ev.preventDefault();
      fire();
      break;
    default:
      break;
  }
});

window.addEventListener("keyup", (ev) => {
  if (ev.code === "ArrowLeft" || ev.code === "ArrowRight") {
    stopAngleHold();
    return;
  }
  if (ev.code !== "Space") return;

  // 게이지를 돌리고 있었을 때만 발사한다. 다른 곳에서 눌렀다 떼거나
  // (blur 등으로) 이미 게이지가 멈춘 뒤 올라온 keyup으로 쏘면 안 된다.
  const wasCharging = powerCharge !== null;
  stopPowerCharge();
  if (wasCharging) fire();
});

// 탭을 벗어나면 keyup을 못 받는다. 그대로 두면 게이지와 각도가 영원히 돈다.
// 여기서는 **발사하지 않는다** — 창을 옮긴 것뿐인데 공이 나가면 안 된다.
window.addEventListener("blur", () => {
  stopPowerCharge();
  stopAngleHold();
});

// ── 터치 조작 (컴팩트 모드) ──────────────────────────────
//
// 슬라이더로는 조준이 **안 된다**. 칸 입구가 66mm, 공이 40mm라 여유가 ±13mm이고,
// 1번 판까지 2.06m이므로 허용 각도 오차는 ±0.36°다. 그런데 −60°~60° 범위를
// 유한한 트랙에 담으면 화면 폭을 다 써도 0.375°/px — 손가락 접점 오차만으로
// 허용치를 몇 배 넘긴다. 트랙을 1900px 넘게 늘려야 하는데 그건 화면에 안 들어간다.
//
// 그래서 **유한 트랙을 버린다.** 조그는 문지른 픽셀만큼 돌고, 손을 떼고 다시
// 문지르면 이어진다 — 트랙 길이가 정밀도를 제한하지 않는다.
//
// 데스크톱이 이 문제를 푼 방식(탭은 정밀 / 홀드는 가속)을 손가락으로 옮긴 게
// 아래 속도 적응형 배율이다: 천천히 문지르면 정밀, 튕기면 판을 가로지른다.

/** 각도 1°당 눈금이 흐르는 픽셀. CSS의 눈금 간격(12px = 1°)과 같은 값이어야 한다. */
const JOG_TICK_PX = 12;

/** 느리게 문지를 때의 배율 (°/px). 12.5px에 한 눈금(0.25°) — 칸 하나를 노릴 수 있다. */
const JOG_GAIN_FINE = 0.02;
/** 튕길 때의 배율 (°/px). 300px 플릭이면 105° — 반대편 판까지 한 번에 간다. */
const JOG_GAIN_COARSE = 0.35;
/** 이 속도(px/ms) 이하는 전부 정밀. */
const JOG_SPEED_FINE = 0.3;
/** 이 속도 이상은 전부 거칢. 사이는 선형 보간. */
const JOG_SPEED_COARSE = 2;

/**
 * 톡 친 것으로 볼 시간 (ms).
 *
 * 발사 버튼은 **길게 누르면 게이지, 톡 치면 맞춰둔 세기**다 —
 * 데스크톱의 Space / Enter와 같은 두 갈래를 버튼 하나에 넣었다.
 * 이 유예가 없으면 톡 친 순간 게이지가 최소치라 0.5로 힘없이 나간다.
 */
const TAP_FIRE_MS = 160;

/**
 * 손가락이 위젯 밖으로 나가도 이벤트를 계속 받는다.
 *
 * 캡처는 **거들 뿐이다** — 없어도 위젯 안에서는 정상 동작한다. 그런데 이게
 * 실패하는 경우가 있고(활성 포인터가 아니거나 요소가 DOM에서 떨어진 경우
 * NotFoundError), 그걸 그대로 두면 예외가 핸들러를 끊어서 **드래그도 게이지도
 * 시작되지 않는다.** 그래서 조작을 먼저 시작하고 캡처는 마지막에, 실패는 삼킨다.
 */
function capturePointer(element: HTMLElement, pointerId: number): void {
  try {
    element.setPointerCapture(pointerId);
  } catch {
    // 캡처 없이 진행한다.
  }
}

function releasePointer(element: HTMLElement, pointerId: number): void {
  if (element.hasPointerCapture(pointerId)) element.releasePointerCapture(pointerId);
}

/** 진행 중인 조그 드래그. 기울기 계산에 직전 위치·시각이 필요하다. */
let jog: { pointerId: number; lastX: number; lastAt: number; acc: number } | null = null;

function stopJog(): void {
  if (jog === null) return;
  releasePointer(el.jogTrack, jog.pointerId);
  jog = null;
  el.jogTrack.classList.remove("active");
}

function clampAngle(degrees: number): number {
  return Math.min(Number(el.angle.max), Math.max(Number(el.angle.min), degrees));
}

el.jogTrack.addEventListener("pointerdown", (ev) => {
  if (!canShootNow()) return;
  ev.preventDefault();
  // 실수값을 따로 들고 간다. range input은 step(0.25)에 스냅되므로 매번 다시
  // 읽으면 반올림 오차가 쌓여 느린 드래그가 아예 안 먹는다 (startAngleHold와 같은 이유).
  jog = { pointerId: ev.pointerId, lastX: ev.clientX, lastAt: ev.timeStamp, acc: Number(el.angle.value) };
  el.jogTrack.classList.add("active");
  capturePointer(el.jogTrack, ev.pointerId);
});

el.jogTrack.addEventListener("pointermove", (ev) => {
  if (jog === null || jog.pointerId !== ev.pointerId) return;
  const dx = ev.clientX - jog.lastX;
  // timeStamp가 같은 프레임에 두 번 오면 0으로 나눈다. 1ms를 바닥으로 둔다.
  const dt = Math.max(1, ev.timeStamp - jog.lastAt);
  jog.lastX = ev.clientX;
  jog.lastAt = ev.timeStamp;

  const ramp = Math.min(
    1,
    Math.max(0, (Math.abs(dx) / dt - JOG_SPEED_FINE) / (JOG_SPEED_COARSE - JOG_SPEED_FINE)),
  );
  jog.acc = clampAngle(jog.acc + dx * (JOG_GAIN_FINE + (JOG_GAIN_COARSE - JOG_GAIN_FINE) * ramp));
  el.angle.value = jog.acc.toFixed(2);
  updateAim();
});

for (const type of ["pointerup", "pointercancel", "lostpointercapture"] as const) {
  el.jogTrack.addEventListener(type, stopJog);
}

/**
 * 조그 양옆의 ‹ › — 마지막 한 눈금을 정확히 맞추는 용도.
 *
 * 키보드 화살표와 **같은 함수**를 쓴다. 톡 누르면 0.25°, 누르고 있으면
 * 22°/s에서 120°/s로 가속한다 — 조작감이 두 입력 사이에서 갈라지지 않는다.
 */
function bindNudge(button: HTMLButtonElement, direction: -1 | 1): void {
  button.addEventListener("pointerdown", (ev) => {
    if (!canShootNow()) return;
    ev.preventDefault();
    nudgeAngle(direction, ANGLE_TAP);
    startAngleHold(direction);
    capturePointer(button, ev.pointerId);
  });
  for (const type of ["pointerup", "pointercancel", "lostpointercapture"] as const) {
    button.addEventListener(type, () => stopAngleHold());
  }
}

bindNudge(el.jogDec, -1);
bindNudge(el.jogInc, 1);

/**
 * 발사 버튼 홀드.
 *
 * `pointerdown`에서 preventDefault를 하면 합성 click이 안 오지만, 브라우저마다
 * 편차가 있어서 플래그로 한 번 더 막는다. 두 번 쏘면 그대로 공 하나를 잃는다.
 */
let firedByPointer = false;
/** 홀드를 시작한 시각. 0이면 홀드 중이 아니다. */
let holdStartedAt = 0;
/** 게이지가 덮어쓰기 전의 세기 — 톡 쳤을 때 되돌린다. */
let holdPowerBefore = "";

el.shoot.addEventListener("pointerdown", (ev) => {
  if (!canShootNow()) return;
  ev.preventDefault();
  firedByPointer = false;
  holdStartedAt = ev.timeStamp;
  holdPowerBefore = el.power.value;
  startPowerCharge();
  capturePointer(el.shoot, ev.pointerId);
});

function endHold(ev: PointerEvent, shouldFire: boolean): void {
  if (holdStartedAt === 0) return;
  const held = ev.timeStamp - holdStartedAt;
  holdStartedAt = 0;
  stopPowerCharge();

  // 톡 친 것 — 게이지는 아직 최소치 근처다. 슬라이더에 맞춰둔 세기로 되돌린다.
  // 취소(pointercancel)일 때도 마찬가지로 되돌려야 다음 샷이 0.5로 나가지 않는다.
  if (!shouldFire || held < TAP_FIRE_MS) {
    el.power.value = holdPowerBefore;
    updateAim();
  }
  if (!shouldFire) return;

  firedByPointer = true;
  fire();
}

el.shoot.addEventListener("pointerup", (ev) => endHold(ev, true));
el.shoot.addEventListener("pointercancel", (ev) => endHold(ev, false));

// 손가락이 버튼 밖으로 나가도 발사한다. 게이지를 보고 떼는 조작이라 손가락
// 위치는 의미가 없고, 데스크톱 Space도 커서 위치와 무관하게 keyup에서 나간다.

el.shoot.addEventListener("click", () => {
  // 위 pointerup이 이미 쐈다. 같은 동작으로 두 번 쏘지 않는다.
  if (firedByPointer) {
    firedByPointer = false;
    return;
  }
  fire();
});

/**
 * 조작 설명을 접는다 — 한 발 쏘고 나면 할 일이 끝났다.
 *
 * 51px이다. 계속 띄워두면 그만큼 테이블이 작아지는데, 이 게임에서 화면은
 * 곧 조준 정밀도다. 조작을 한 번이라도 해봤으면 설명보다 테이블이 낫다.
 * 다시 온 사람에게도 안 띄운다 — 조그와 ‹ ›는 한 번 써보면 자명하다.
 */
function retireTouchHint(): void {
  if (el.keyHint.classList.contains("seen")) return;
  el.keyHint.classList.add("seen");
  localStorage.setItem("scb.touchHintSeen", "1");
  measureChrome();
}

// ── 규칙 시트 ───────────────────────────────────────────
//
// 규칙을 캔버스 위에 상시 띄우면 390px 화면에서 268px — 테이블 폭의 3/4을 덮는다.
// 규칙은 한 번 읽고 마는 정보지 상시 표시할 정보가 아니므로,
// 독의 [규칙] 버튼으로 여는 전체화면 시트로 통일했다.

function openRulesSheet(open: boolean): void {
  el.rules.classList.toggle("open", open);
}

el.rulesBtn.addEventListener("click", () => openRulesSheet(true));
el.rulesClose.addEventListener("click", () => openRulesSheet(false));

// ── 화면 치수 ───────────────────────────────────────────

/**
 * 상·하단 패널이 실제로 차지하는 높이를 재서 CSS 변수로 넘긴다.
 *
 * `#stage`가 그만큼 비켜서므로 **캔버스가 패널에 가려지지 않는다**. 높이를 CSS에
 * 상수로 박으면 글자 크기·줄바꿈·safe-area·위치 슬라이더 유무에 따라 어긋난다.
 *
 * Pixi의 `resizeTo`는 window resize만 듣기 때문에, 독 높이만 바뀐 경우에는
 * 직접 깨워줘야 캔버스가 따라온다.
 */
function measureChrome(): void {
  const root = document.documentElement.style;
  // offsetHeight는 숨겨진 패널(.hidden)에서 0이므로 로비 화면도 저절로 맞는다.
  root.setProperty("--hud-h", `${el.hud.offsetHeight}px`);
  root.setProperty("--dock-h", `${el.controls.offsetHeight}px`);
  // 독 위에 얹히는 줄들. 둘이 동시에 뜨는 경우는 없지만 합쳐도 값은 맞는다.
  const extra = el.practice.offsetHeight + el.finalSequence.offsetHeight;
  root.setProperty("--extra-h", extra > 0 ? `${extra + 8}px` : "0px");
  renderer.resize();
}

/**
 * 글자 크기·줄바꿈처럼 코드가 모르는 이유로 패널 높이가 변할 때의 보정.
 *
 * 확실한 갱신은 `applyState`/`quit`이 순서를 맞춰서 한다 — 이건 그물이다.
 * (백그라운드 탭에서는 렌더링 스텝이 멈춰 콜백이 아예 안 온다. 그래도 괜찮다.
 * 안 보이는 동안은 잴 이유가 없고, 돌아오면 그때 한 번 온다.)
 *
 * 참조를 붙들어 둔다 — 어디에도 안 매달린 옵저버는 수거될 수 있다.
 */
const chromeObserver = new ResizeObserver(() => measureChrome());
for (const panel of [el.hud, el.controls, el.practice, el.finalSequence]) {
  chromeObserver.observe(panel);
}

// 한 번 써 본 조작 힌트는 다시 안 띄운다 (retireTouchHint가 저장해 둔다).
el.keyHint.classList.toggle("seen", localStorage.getItem("scb.touchHintSeen") === "1");
// 규칙 시트에는 접기가 없다 (summary를 숨긴다). 펼쳐 두지 않으면 빈 시트가 열린다.
el.rulesBody.open = true;
measureChrome();

el.bank.addEventListener("click", () => {
  session?.send(ClientMessage.BankPoints);
  el.finalSequence.classList.add("hidden");
});

el.continue.addEventListener("click", () => {
  session?.send(ClientMessage.ContinueFinalSequence);
  el.finalSequence.classList.add("hidden");
});

// ── 유틸 ───────────────────────────────────────────────

function teamLabel(team: number): string {
  return team === 0 ? "주황" : "청록";
}

function phaseLabel(state: BattleView): string {
  if (session?.kind === "practice") {
    return state.phase === "ended" ? "세트 종료" : "연습";
  }
  const remain = Math.max(0, Math.ceil(((state.phaseEndsAt ?? 0) - Date.now()) / 1000));
  switch (state.phase) {
    case "lobby":
      return "대기실";
    case "countdown":
      return "곧 시작합니다…";
    case "round":
      return `라운드 ${state.round} · ${formatClock(remain)}`;
    case "intermission":
      return "라운드 사이 (자리 교대)";
    case "overtime":
      return `연장전 · ${formatClock(remain)}`;
    case "ended":
      return "경기 종료";
    default:
      return state.phase ?? "—";
  }
}

function formatClock(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function roundEndLabel(reason: RoundEndReason): string {
  switch (reason) {
    case "grand_slam":
      return "만관";
    case "out_of_balls":
      return "공 소진";
    case "banked":
      return "점수 환산";
    default:
      return "시간 종료";
  }
}

function reasonLabel(reason: string | null): string {
  switch (reason) {
    case "miss":
      return "실패";
    case "grouping":
      return "그루핑 (득점 없이 정지)";
    case "opponent_scored":
      return "상대 공이 들어감";
    case "out_of_balls":
      return "공 소진";
    case "timeout":
      return "시간 초과";
    case "banked":
      return "점수 환산";
    default:
      return "턴 종료";
  }
}

/**
 * 리플레이 재생 중에 도착한 로그.
 *
 * 서버(로컬 세션 포함)는 샷 결과 직후에 라운드 종료·경기 종료를 바로 이어 보낸다.
 * 그대로 찍으면 공이 아직 굴러가는 중에 "라운드 N 종료 — 3 : 1"이 먼저 뜬다.
 * 그래서 재생이 끝날 때까지 모아뒀다가 도착 순서대로 flushLog()로 내보낸다.
 */
const pendingLog: string[] = [];

function log(text: string): void {
  if (shotInFlight) {
    pendingLog.push(text);
    return;
  }
  emitLog(text);
}

/** 재생이 끝난 뒤 밀려 있던 로그를 도착 순서대로 내보낸다. */
function flushLog(): void {
  const queued = pendingLog.splice(0, pendingLog.length);
  for (const text of queued) emitLog(text);
}

function clearLog(): void {
  pendingLog.length = 0;
  el.log.replaceChildren();
}

function emitLog(text: string): void {
  const div = document.createElement("div");
  div.textContent = text;
  el.log.prepend(div);
  while (el.log.childElementCount > 6) el.log.lastElementChild?.remove();
}

// 페이즈 시계와 턴 카운트다운을 주기적으로 갱신 (둘 다 초 단위 표시)
setInterval(() => {
  if (!session) return;
  el.phase.textContent = phaseLabel(session.state);
  updateTurnClock();
}, 250);

// ── 렌더러 기동 ─────────────────────────────────────────
//
// ⚠️ **최상위 await 금지.**
//
// 프로덕션 빌드에서 Rollup은 Pixi 공유 코드를 이 entry 청크에 넣는다. Pixi의
// autoDetectRenderer는 WebGLRenderer 청크를 동적 import하는데, 그 청크의 첫 줄은
// 다시 이 entry 청크를 static import한다. 최상위 await로 모듈 평가를 멈춘 채
// app.init()을 기다리면 두 청크가 서로의 평가 완료를 기다리는 데드락이 되고,
// 캔버스는 영영 생기지 않는다. dev 서버는 번들링이 없어 멀쩡하고 **빌드에서만
// 터진다** — 실제로 배포판이 이걸로 전원 접속 불가가 됐다.
//
// 그래서 부팅은 파일 맨 끝의 async IIFE로 돈다. 로비 배선은 이 위에 있고
// (캔버스가 준비되기 전에도 모드 선택은 돼야 한다), 실제로 판을 여는
// **시작 버튼만** 준비될 때까지 잠가 둔다.
/**
 * 탭이 화면에 보일 때까지 기다린다.
 *
 * 숨겨진 탭에서는 rAF가 멈춰 있어 그릴 이유가 없다. 보일 때 열어주고,
 * 그동안은 상태 문구로 대신한다.
 */
function whenVisible(): Promise<void> {
  if (!document.hidden) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const onChange = (): void => {
      if (document.hidden) return;
      document.removeEventListener("visibilitychange", onChange);
      resolve();
    };
    document.addEventListener("visibilitychange", onChange);
  });
}

/** 초기화가 이만큼 안 끝나면 한 번 멈춰 서서 탭이 아직 보이는지 다시 확인한다. */
const GPU_WAIT_MS = 4000;

/**
 * 캔버스를 띄운다.
 *
 * init은 **한 번만 걸어둔다** — 두 번 부르면 Pixi가 이중 초기화로 깨진다.
 * 정상적인 기기에선 수십 ms에 끝나지만, 저사양 폰의 첫 GPU 초기화는 몇 초씩
 * 걸릴 수 있으니 포기하지 않고 기다린다. 대신 오래 걸리면 말은 해준다.
 * (과거에 "숨겨진 탭에서는 init이 안 끝난다"고 기록했던 현상의 실체는
 * 위의 청크 순환 데드락이었다 — 가시성과 무관하게 빌드판 전체가 멈춘 것.)
 */
async function bootRenderer(): Promise<boolean> {
  const ready = renderer.init(el.stage).then(
    () => true,
    (err: unknown) => {
      // 지금까지는 실패해도 조용히 멈추기만 했다. 최소한 흔적은 남긴다.
      console.error("[scb] 렌더러 초기화 실패", err);
      return null;
    },
  );

  let stalled = false;
  for (;;) {
    if (document.hidden) {
      el.status.textContent = "이 탭을 열면 시작합니다";
      await whenVisible();
      stalled = false;
    }
    el.status.textContent = stalled
      ? "화면이 늦어지고 있어요. 계속 이러면 새로고침 해주세요"
      : "화면 준비 중…";

    const outcome = await Promise.race([
      ready,
      new Promise<"timeout">((resolve) => setTimeout(() => resolve("timeout"), GPU_WAIT_MS)),
    ]);
    if (outcome === true) return true;
    if (outcome === null) {
      el.status.textContent = "화면을 열지 못했습니다. 새로고침 해주세요";
      return false;
    }
    stalled = true;
  }
}

void (async () => {
  if (await bootRenderer()) {
    renderer.setBalls([]);

    syncSeat(null);
    updateAim();

    el.join.disabled = false;
    el.status.textContent = inviteCode
      ? `친구가 초대한 방 ${inviteCode} — 닉네임을 정하고 입장하세요`
      : "";
  }
})();
